import React, { useState, useEffect, useCallback, useContext } from "react";
import "./watchlist.css";
import Rating from "@mui/material/Rating";
import Button from "@mui/material/Button";
import { Heart, Trash2, ShoppingCart, ShoppingBag, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { MyContext } from "../../App";
import { fetchDataFromApi, deleteData } from "../../utils/api";

const Wishlist = () => {
    const context = useContext(MyContext);
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const userId = context.user?.userId || context.user?._id;

    const fetchWishlist = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchDataFromApi(`/api/watchlist/${userId}`);
            if (data?.success) {
                setWishlistItems(data.data || []);
            }
        } catch (error) {
            console.error("Fetch wishlist error:", error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchWishlist();
    }, [fetchWishlist]);

    const deleteItem = async (itemId) => {
        try {
            const result = await deleteData(`/api/watchlist/${itemId}`);
            if (result?.success) {
                setWishlistItems((prevItems) =>
                    prevItems.filter((item) => item._id !== itemId)
                );
                context.setAlertBox({
                    open: true,
                    error: false,
                    msg: "Item removed from wishlist"
                });
                if (context.refreshWatchlist) context.refreshWatchlist();
            }
        } catch (error) {
            console.error("Delete item error:", error);
        }
    };

    const clearAllItems = async () => {
        if (window.confirm("Are you sure you want to clear your entire wishlist?")) {
            try {
                const result = await deleteData(`/api/watchlist/clear/${userId}`);
                if (result?.success) {
                    setWishlistItems([]);
                    context.setAlertBox({
                        open: true,
                        error: false,
                        msg: "Wishlist cleared successfully"
                    });
                    if (context.refreshWatchlist) context.refreshWatchlist();
                }
            } catch (error) {
                console.error("Clear wishlist error:", error);
            }
        }
    };

    if (loading) {
        return (
            <div className="loader-container d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-center"></div>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="empty-wishlist text-center py-5">
                <div className="icon-box mb-4">
                    <Heart size={80} color="#ccc" strokeWidth={1} />
                </div>
                <h2>Please Log In</h2>
                <p className="text-muted">You need to be logged in to view your wishlist.</p>
                <Link to="/login" className="mt-3 d-inline-block">
                    <Button variant="contained" className="bg-success">Go to Login</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="wishlist-page mb-5">
            <div className="container-fluid">
                <div className="wishlist-header d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h2 className="hd mb-0">My Wishlist</h2>
                        <p className="text-muted mb-0">There are <span className="text-success font-weight-bold">{wishlistItems.length}</span> products in your wishlist</p>
                    </div>
                    {wishlistItems.length > 0 && (
                        <Button
                            variant="text"
                            color="error"
                            onClick={clearAllItems}
                            startIcon={<Trash2 size={18} />}
                            className="clear-btn"
                        >
                            Clear All
                        </Button>
                    )}
                </div>

                {wishlistItems.length === 0 ? (
                    <div className="empty-wishlist-box text-center py-5 shadow-sm rounded bg-white">
                        <ShoppingBag size={100} className="text-light mb-4" strokeWidth={1} />
                        <h3 className="mb-2">Your wishlist is empty</h3>
                        <p className="text-muted mb-4">Save items you're interested in to view them here.</p>
                        <Button
                            variant="contained"
                            className="bg-success text-white px-4 py-2"
                            onClick={() => navigate("/")}
                            style={{ borderRadius: '30px', textTransform: 'none' }}
                        >
                            Continue Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="wishlist-table-wrapper shadow-sm rounded bg-white overflow-hidden">
                        <table className="table wishlist-table mb-0">
                            <thead>
                                <tr>
                                    <th className="pl-4">Product</th>
                                    <th>Price</th>
                                    <th>Stock Status</th>
                                    <th>Rating</th>
                                    <th className="text-center pr-4">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wishlistItems.map((item) => (
                                    <tr key={item._id} className="align-middle">
                                        <td className="product-col pl-4 py-4">
                                            <div className="d-flex align-items-center">
                                                <div className="product-img-box mr-3">
                                                    <img src={item.images[0]} alt={item.productTitle} />
                                                </div>
                                                <div className="product-info">
                                                    <Link to={`/details/${item.productId}`} className="product-name d-block">
                                                        {item.productTitle}
                                                    </Link>
                                                    <span className="brand text-muted small">{item.brand || "N/A"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="price-col">
                                            <span className="current-price text-success font-weight-bold">₹{item.price}</span>
                                            {item.oldPrice && <span className="old-price ml-2 text-muted small text-decoration-line-through">₹{item.oldPrice}</span>}
                                        </td>
                                        <td className="stock-col">
                                            <span className="badge badge-success-light px-3 py-2 rounded-pill">In Stock</span>
                                        </td>
                                        <td className="rating-col">
                                            <div className="d-flex align-items-center">
                                                <Rating value={item.rating || 0} precision={0.5} readOnly size="small" />
                                                <span className="ml-2 text-muted small">({item.rating || 0})</span>
                                            </div>
                                        </td>
                                        <td className="action-col text-center pr-4">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <Link to={`/details/${item.productId}`}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        className="bg-success text-white action-btn"
                                                        style={{ borderRadius: '20px', minWidth: '100px' }}
                                                    >
                                                        Details
                                                    </Button>
                                                </Link>
                                                <IconButton
                                                    onClick={() => deleteItem(item._id)}
                                                    className="delete-icon-btn text-danger"
                                                >
                                                    <Trash2 size={20} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const IconButton = ({ children, onClick, className }) => (
    <button onClick={onClick} className={`btn btn-link p-2 ${className}`} style={{ textDecoration: 'none' }}>
        {children}
    </button>
);

export default Wishlist;
